import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, Download, Infinity, Smartphone, Languages, Zap } from "lucide-react";

const includes = [
  { Icon: Download, text: "Instant download" },
  { Icon: Infinity, text: "Lifetime access" },
  { Icon: Smartphone, text: "Mobile friendly PDF" },
  { Icon: Languages, text: "Hindi + English explanations" },
];

export default function Pricing({ onCTAClick }) {
  return (
    <section id="pricing" className="py-20 md:py-28 bg-slate-50 relative overflow-hidden" data-testid="pricing-section">
      <div className="absolute inset-0 dotted-bg opacity-50" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">
            Limited Time Offer
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1E3A8A] leading-tight" style={{ fontFamily: "Outfit" }}>
            One small price.<br />
            <span className="text-emerald-600">A lifetime of relief.</span>
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-[#1E3A8A] rounded-[2.5rem] p-8 sm:p-12 text-white shadow-2xl shadow-blue-900/30 overflow-hidden"
          data-testid="pricing-card"
        >
          {/* Decorative gradient */}
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-orange-400/20 blur-3xl" />

          {/* Badge */}
          <div className="relative inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/20 border border-orange-300/30 mb-6">
            <Zap className="w-3.5 h-3.5 text-orange-300 fill-orange-300" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-orange-200">
              60% OFF · Today Only
            </span>
          </div>

          <h3 className="relative text-2xl sm:text-3xl font-black mb-2" style={{ fontFamily: "Outfit" }}>
            Back Pain Relief Guide
          </h3>
          <p className="relative text-blue-100/80 mb-8 text-sm sm:text-base">
            The complete doctor-approved PDF guide
          </p>

          {/* Price */}
          <div className="relative flex items-end gap-3 mb-8">
            <div className="flex items-start">
              <span className="text-2xl sm:text-3xl font-bold text-white/70 mt-3">₹</span>
              <span className="text-6xl sm:text-7xl lg:text-8xl font-black text-white leading-none" style={{ fontFamily: "Outfit" }} data-testid="price-current">
                199
              </span>
            </div>
            <div className="flex flex-col mb-2">
              <span className="text-lg text-blue-200/70 line-through font-medium" data-testid="price-original">
                ₹499
              </span>
              <span className="text-xs font-bold text-emerald-300 uppercase tracking-wider">
                Save ₹300
              </span>
            </div>
          </div>

          {/* Includes */}
          <ul className="relative space-y-3 mb-10">
            {includes.map(({ Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4 text-emerald-300" strokeWidth={3} />
                </div>
                <span className="text-base sm:text-lg text-white/95 font-medium">{text}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <Button
            onClick={onCTAClick}
            className="relative w-full h-16 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-base sm:text-lg font-bold cta-glow transition-all hover:-translate-y-0.5"
            data-testid="pricing-cta-button"
          >
            Buy Now for ₹199
            <Zap className="w-5 h-5 ml-2 fill-white" />
          </Button>

          <p className="relative text-center text-xs text-blue-100/70 mt-4">
            Secure checkout via Razorpay · PDF delivered on WhatsApp
          </p>
        </motion.div>
      </div>
    </section>
  );
}
